import { FC, useMemo, useState } from 'react'

import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { isEqual, uniqWith } from 'lodash-es'

import {
  Alert,
  Avatar,
  AvatarFallback,
  Button,
  Cluster,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icon,
  IconButton,
  Spinner,
  Stack,
  Table,
  Typography,
  useToaster,
} from '@forto-dev/phoenix-ui-core'
import { Layout, Widget, WidgetFooter, WidgetHeader, WidgetTitle } from '@forto-dev/phoenix-ui-core/ship'

import { useAuth } from '@/features/auth'
import { customerDocumentsQueryOptions } from '@/features/company/api/useCompanyDocuments'
import { Footer } from '@/fragments/footer'
import { Header } from '@/fragments/header'
import { Navigation } from '@/fragments/navigation'
import { isPreviewableExtension } from '@/utils/documentPreview'
import { getMicrosoftPreviewUrlIfApplicable } from '@/utils/documentPreviewUrl'
import { getInitialsForName } from '@/utils/initials'
import { isValidTranslationKey, useLocalisation } from '@/utils/localisation'

import { DocumentUpload } from './upload'
import {
  documentTemplatesQueryOptions,
  getEntityDocument,
  triggerDownloadForDocumentTemplate,
} from '../api/documents.service'
import { useDocumentDelete } from '../api/useDocumentDelete'

type UnifiedDocument = {
  id: string
  name: string
  date: string
  type?: string
  documentType?: {
    code: string
    displayName: string
  }
  documentTypeCode?: string
  uploadedBy?: string
  mimeType: string
  download?: () => Promise<void>
  deletable?: boolean
}

type FilterType = 'all' | 'templates' | 'user_provided'

type Filter = {
  items: UnifiedDocument[]
}

const useCompanyDocumentsTable = (
  setCurrentPreviewDocument: (previewDoc: UnifiedDocument) => void,
  setCurrentDeleteDocument: (deleteDoc: UnifiedDocument) => void
) => {
  const { translate, translateDate } = useLocalisation()
  return [
    {
      id: 'name',
      header: translate('company-documents.table.name'),
      accessorKey: 'name',
      enableSorting: true,
      cell: ({ row: { original } }: { row: { original: UnifiedDocument } }) => {
        return (
          <Typography variant="small" truncate>
            {original.name}
          </Typography>
        )
      },
      meta: {
        style: {
          width: '30%',
        },
      },
    },
    {
      id: 'date',
      header: translate('company-documents.table.date'),
      accessorKey: 'date',
      enableSorting: true,
      cell: ({
        row: { original },
      }: {
        row: {
          original: UnifiedDocument
        }
      }) => {
        const originalDate = new Date(original.date)
        if (!isNaN(originalDate.getTime())) {
          return translateDate('company_documents_date', originalDate)
        }
        return '--'
      },
      meta: {
        style: {
          width: '130px',
        },
      },
    },
    {
      id: 'uploader',
      header: translate('company-documents.table.uploader'),
      accessorKey: 'uploadedBy',
      enableSorting: true,
      cell: ({ row: { original } }: { row: { original: UnifiedDocument } }) => {
        if (!original.uploadedBy) {
          return null
        }
        return (
          <Cluster gap={8} y="center">
            <Avatar size="sm">
              <AvatarFallback>{getInitialsForName(original.uploadedBy)}</AvatarFallback>
            </Avatar>
            <Typography variant="small" truncate>
              {original.uploadedBy}
            </Typography>
          </Cluster>
        )
      },
    },
    {
      id: 'actions',
      header: '',
      accessor: 'actions',
      cell: ({ row: { original } }: { row: { original: UnifiedDocument } }) => {
        return (
          <Cluster gap={16} x="end" y="center">
            {(() => {
              const extension = original.name.split('.').pop()?.toLowerCase() || ''
              return isPreviewableExtension(extension)
            })() ? (
              <IconButton
                color="primary"
                icon="View"
                size="sm"
                onClick={() => setCurrentPreviewDocument(original)}
                label={translate('company-documents.actions.view')}
              />
            ) : null}
            {original.download ? (
              <IconButton
                color="primary"
                icon="Download"
                size="sm"
                onClick={() => original.download?.()}
                label={translate('company-documents.actions.download')}
              />
            ) : null}
            {original.deletable ? (
              <IconButton
                color="negative"
                icon="Bin"
                size="sm"
                onClick={() => setCurrentDeleteDocument(original)}
                label={translate('company-documents.actions.delete')}
              />
            ) : null}
          </Cluster>
        )
      },
      meta: {
        style: {
          width: '100px',
        },
      },
    },
  ]
}

export const Documents: FC = () => {
  const { user, initialData } = useAuth()
  const toaster = useToaster()
  const queryClient = useQueryClient()
  const { translate, currentLanguage } = useLocalisation()
  const deleteDocumentMutation = useDocumentDelete()
  const { data: documentTemplates } = useSuspenseQuery(documentTemplatesQueryOptions())
  const [view, setView] = useState<FilterType>('all')
  const [currentPreviewDocument, setCurrentPreviewDocument] = useState<UnifiedDocument | undefined>(undefined)
  const [currentPreviewDocumentUrl, setCurrentPreviewDocumentUrl] = useState<string | undefined>(undefined)
  const columns = useCompanyDocumentsTable(
    async (previewDoc: UnifiedDocument) => {
      setCurrentPreviewDocument(previewDoc)
      if (previewDoc.id && initialData.data?.customer._id) {
        const msftPreviewUrl = await getMicrosoftPreviewUrlIfApplicable(previewDoc.id, previewDoc.name)

        if (msftPreviewUrl) {
          setCurrentPreviewDocumentUrl(msftPreviewUrl)
        } else {
          const docData = await getEntityDocument(initialData.data.customer._id, previewDoc.id, previewDoc.name)
          const url = URL.createObjectURL(new Blob([docData.blob], { type: previewDoc.mimeType }))
          setCurrentPreviewDocumentUrl(url)
        }
      }
    },
    (deleteDoc: UnifiedDocument) => setCurrentDeleteDocument(deleteDoc)
  )
  const [currentDeleteDocument, setCurrentDeleteDocument] = useState<UnifiedDocument | undefined>(undefined)
  const { data: documents } = useSuspenseQuery(customerDocumentsQueryOptions(initialData.data!.customer._id))

  const filters = useMemo(() => {
    const templates: UnifiedDocument[] = documentTemplates
      .filter((documentTemplate) => documentTemplate.files.length > 0)
      .map((documentTemplate) => {
        const language = currentLanguage.substring(0, 2)
        const file = documentTemplate.files.find((item) => item.language === language) || documentTemplate.files[0]

        return {
          id: documentTemplate.id,
          name: documentTemplate.displayName,
          date: '--',
          documentTypeCode: documentTemplate.documentTypeCode,
          documentType: documentTemplate.documentType,
          uploadedBy: '',
          mimeType: 'application/pdf',
          download: file
            ? async () => triggerDownloadForDocumentTemplate(documentTemplate.id, file.language, file.fileName)
            : undefined,
        }
      })
    const userProvided: UnifiedDocument[] = (documents ?? []).map((document) => ({
      id: document.id,
      name: document.fileName,
      date: document.createdAt,
      documentType: document.documentType,
      documentTypeCode: document.documentTypeCode,
      uploadedBy: !document.createdBy.id.startsWith('U-') ? 'Forto' : document.createdBy.email,
      mimeType: document.mimeType,
      deletable: true,
    }))

    return {
      all: {
        items: [...templates, ...userProvided],
      },
      templates: {
        items: templates,
      },
      user_provided: {
        items: userProvided,
      },
    } satisfies Record<FilterType, Filter>
  }, [currentLanguage, documentTemplates, documents])
  const filteredDocuments = useMemo(() => filters[view].items, [filters, view])
  const documentTypes = uniqWith(
    filteredDocuments.map((document) => document.documentType).filter((documentType) => !!documentType),
    isEqual
  )

  const clusters = documentTypes.map((documentType) => {
    const docTypeTranslationKey = `documents.types.${documentType.code}`
    return {
      code: documentType.code,
      name: isValidTranslationKey(docTypeTranslationKey) ? translate(docTypeTranslationKey) : documentType.displayName,
      items: filteredDocuments.filter((document) => document.documentTypeCode === documentType.code),
    }
  })
  return (
    <>
      <Layout
        header={<Header />}
        navigation={<Navigation />}
        title={<Typography variant="h4">{translate('company-documents.title')}</Typography>}
        sidebar={
          <Stack gap={16}>
            {Object.entries(filters).map(([key, filter]) => (
              <Widget
                key={key}
                variant={key === view ? 'primary' : 'neutral'}
                onClick={() => setView(key as FilterType)}
              >
                <WidgetHeader>
                  <WidgetTitle>{translate(`company-documents.categories.${key as FilterType}`)}</WidgetTitle>
                </WidgetHeader>
                <WidgetFooter>
                  <Cluster gap={4} y="center">
                    <Icon size="md" name="Document" />
                    <Typography variant="h3">{filter.items.length}</Typography>
                  </Cluster>
                  <Icon size="sm" name="ArrowRight" color="default" />
                </WidgetFooter>
              </Widget>
            ))}
          </Stack>
        }
        main={
          <Stack gap={16}>
            <DocumentUpload />
            <Stack gap={16}>
              {!filteredDocuments.length ? (
                <Alert title={translate('company-documents.filters.empty')} />
              ) : (
                clusters.map((cluster) => {
                  return (
                    <Stack key={cluster.code} gap={8}>
                      <Cluster gap={4} y="center">
                        <Icon name="Document" />
                        <Typography variant="body">{cluster.name}</Typography>
                      </Cluster>
                      <Table columns={columns} data={cluster.items} />
                    </Stack>
                  )
                })
              )}
            </Stack>
          </Stack>
        }
        footer={<Footer />}
      />

      {currentPreviewDocument && currentPreviewDocumentUrl ? (
        <Dialog
          defaultOpen
          onOpenChange={(isOpen) => {
            if (!isOpen) {
              setCurrentPreviewDocument(undefined)
              setCurrentPreviewDocumentUrl(undefined)
            }
          }}
        >
          <DialogContent maxWidth="full" maxHeight="full">
            <DialogHeader>
              <DialogTitle>{translate('company-documents.preview.title')}</DialogTitle>
            </DialogHeader>
            <iframe
              src={currentPreviewDocumentUrl}
              width="100%"
              // eslint-disable-next-line react/forbid-dom-props
              style={{ height: '95vh' }}
            ></iframe>
          </DialogContent>
        </Dialog>
      ) : null}

      {currentDeleteDocument ? (
        <Dialog
          defaultOpen
          onOpenChange={(isOpen) => {
            if (!isOpen) {
              setCurrentDeleteDocument(undefined)
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{translate('company-documents.delete.title')}</DialogTitle>
            </DialogHeader>
            <Typography>
              {translate('company-documents.delete.message', {
                name: currentDeleteDocument.name,
              })}
            </Typography>
            <DialogFooter>
              <Button
                variant="outline-primary"
                onClick={() => setCurrentDeleteDocument(undefined)}
                disabled={deleteDocumentMutation.isPending}
              >
                {translate('company-documents.delete.actions.cancel')}
              </Button>
              <Button
                disabled={deleteDocumentMutation.isPending}
                onClick={async () => {
                  if (user) {
                    try {
                      await deleteDocumentMutation.mutateAsync({
                        customerId: user.customer_id ?? '?',
                        documentId: currentDeleteDocument.id,
                      })

                      await queryClient.invalidateQueries({
                        queryKey: ['customerDocuments', initialData.data!.customer._id],
                      })

                      toaster({
                        title: translate('company-documents.delete.notification.deleted'),
                      })
                      setCurrentDeleteDocument(undefined)
                    } catch (error) {
                      toaster({
                        title: translate('company-documents.delete.notification.error'),
                        // @ts-expect-error TODO: handle error
                        description: error?.message,
                        variant: 'negative',
                      })
                    }
                  }
                }}
              >
                {deleteDocumentMutation.isPending ? (
                  <Spinner />
                ) : (
                  <Cluster gap={8} y="center">
                    {translate('company-documents.delete.actions.delete')}
                  </Cluster>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  )
}
